import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { ClientCreatePayload, ClientUpdatePayload, CognitoUser, User } from '@mira-avant/mtlibs';
import { LanguageService } from './language.service';
import { Client, _ } from '@mira-avant/mtlibs';
import { CourseService } from './course.service';
import { UrlHelper } from '../utils/url.helper';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { LoggedInService } from './logged-in.service';
import { NavigationService } from './navigation.service';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root',
})
export class ClientService {
	private readonly BASE_PATH = 'clients';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public clientsBs$: BehaviorSubject<Client[]> = new BehaviorSubject<Client[]>([]);
	public clients$: Observable<Client[]> = this.clientsBs$.pipe(filter((clients: Client[]) => clients.length >= 1));

	public selectedClientBs$: BehaviorSubject<Client | undefined> = new BehaviorSubject<Client | undefined>(undefined);

	public selectedClient$: Observable<Client> = this.selectedClientBs$.pipe(
		filter((selectedClient: Client | undefined): selectedClient is Client => selectedClient !== undefined)
	);

	//Store the most recent client uuid that was selected by the user
	private pendingSelectedClientUuid: string | undefined;

	constructor(
		private authService: AuthService,
		private loggedInService: LoggedInService,
		private api: ApiService,
		private languageService: LanguageService,
		private courseService: CourseService,
		private nav: NavigationService
	) {
		this.authService.userBs$.subscribe((user: CognitoUser | null) => {
			user ? this.initializeClients() : this.reset();
		});

		//If we logout clear our state
		this.loggedInService.isLoggedIn$.subscribe((isLoggedIn: boolean) => {
			if (!isLoggedIn) {
				this.reset();
			}
		});
	}

	private reset(): void {
		this.clientsBs$.next([]);
		this.selectedClientBs$.next(undefined);
		this.pendingSelectedClientUuid = undefined;
	}

	public async initializeClients(): Promise<void> {
		const clients = await this.getAll$();
		this.clientsBs$.next(clients);
	}

	public setActiveText(client: Client): void {
		const currentLang: string = this.languageService.spokenLanguage$.value;
		const activeText = client.client_text.find(clientText => clientText.default_lang_id === currentLang);
		client.active_text = activeText || client.client_text[0];
	}

	public sortClients(clients: Client[]): Client[] {
		return clients.sort((a: Client, b: Client) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}

			if (!a.active_text || !b.active_text) {
				return 0;
			}

			return a.active_text.name.localeCompare(b.active_text.name);
		});
	}

	private processClient(client: Client): Client {
		this.setActiveText(client);

		if (client.course && Array.isArray(client.course)) {
			client.course = this.courseService.processCourses(client.course);
		}

		return client;
	}

	private processClients(clients: Client[]): Client[] {
		return this.sortClients(clients.map(client => this.processClient(client)));
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getAll$(expandChildren: boolean = false, expandAll: boolean = false): Promise<Client[]> {
		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const clients = await firstValueFrom(this.api.get<Client[]>(this.BASE_PATH + '/all', { params }));

			const processedClients = this.processClients(clients);

			this.clientsBs$.next(processedClients);

			return processedClients;
		});
	}

	public async get$(uuid: string, expandChildren: boolean = false, expandAll: boolean = false): Promise<Client> {
		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const client = await firstValueFrom(
				this.api.get<Client>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params })
			);
			return this.processClient(client);
		});
	}

	public async isEduskillsAllowed$(): Promise<boolean> {
		return this.withLoading(async () => {
			const subdomain: string = UrlHelper.getLeftMostSubDomains();

			const result = await firstValueFrom(
				this.api.get<boolean>(`${this.BASE_PATH}/is_eduskills_allowed/${subdomain}`)
			);

			return result;
		});
	}

	public async isSelfRosteringAllowed$(): Promise<boolean> {
		return this.withLoading(async () => {
			const subdomain: string = UrlHelper.getLeftMostSubDomains();

			const result = await firstValueFrom(
				this.api.get<boolean>(`${this.BASE_PATH}/is_self_rostering_allowed/${subdomain}`)
			);

			return result;
		});
	}

	public async isCleverSsoAllowed$(): Promise<boolean> {
		return this.withLoading(async () => {
			const subdomain: string = UrlHelper.getLeftMostSubDomains();

			const result = await firstValueFrom(
				this.api.get<boolean>(`${this.BASE_PATH}/is_clever_sso_allowed/${subdomain}`)
			);

			return result;
		});
	}

	// public async getClientWithCoursesAndUnits$(uuid: string): Promise<Client> {
	// 	return this.withLoading(async () => {
	// 		const client = await firstValueFrom(
	// 			this.api.get<Client>(`${this.BASE_PATH}/client-course-unit/${uuid}`)
	// 		);
	// 		return this.processClient(client);
	// 	});
	// }

	public async getClientWithCoursesAndUnitsAndLessons$(uuid: string): Promise<Client> {
		return this.withLoading(async () => {
			const client = await firstValueFrom(
				this.api.get<Client>(`${this.BASE_PATH}/client-course-unit-lesson/${uuid}`)
			);
			return this.processClient(client);
		});
	}

	// public async getClientWithCoursesAndUnitsAndLessonsAndActivities$(
	// 	uuid: string
	// ): Promise<Client> {
	// 	return this.withLoading(async () => {
	// 		const client = await firstValueFrom(
	// 			this.api.get<Client>(`${this.BASE_PATH}/client-course-unit-lesson-activity/${uuid}`)
	// 		);
	// 		return this.processClient(client);
	// 	});
	// }

	public async create$(clientData: ClientCreatePayload): Promise<Client> {
		return this.withLoading(async () => {
			const newClient = await firstValueFrom(this.api.post<Client>(this.BASE_PATH, clientData));
			const processedClient = this.processClient(newClient);
			const currentClients = this.clientsBs$.value;
			const updatedClients = this.processClients([...currentClients, processedClient]);
			this.clientsBs$.next(updatedClients);
			return processedClient;
		});
	}

	public async update$(uuid: string, clientData: ClientUpdatePayload): Promise<Client> {
		return this.withLoading(async () => {
			const updatedClient = await firstValueFrom(this.api.put<Client>(`${this.BASE_PATH}/${uuid}`, clientData));
			const processedClient = this.processClient(updatedClient);
			const currentClients = this.clientsBs$.value;
			const updatedClients = this.processClients(
				currentClients.map(client => (client.uuid === uuid ? processedClient : client))
			);
			this.clientsBs$.next(updatedClients);
			return processedClient;
		});
	}

	// The setup wizard allows end-users to update SOME details (not all details)
	// about their client (also called 'account' in the setup wizard).
	// Once the client is updated and this method is called we need to refresh
	// the client data in memory in clientsBs$ and selectedClientBs$
	// by the time this is called the client in the database has already been updated.
	public async updateFromSetupWizard$(clientUuid: string): Promise<void> {
		const updatedClient = await this.getClientWithCoursesAndUnitsAndLessons$(clientUuid);
		const currentClients = this.clientsBs$.value;
		const updatedClients = currentClients.map(client => (client.uuid === clientUuid ? updatedClient : client));
		this.clientsBs$.next(updatedClients);
		this.selectedClientBs$.next(updatedClient);
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
			const currentClients = this.clientsBs$.value;
			const updatedClients = currentClients.filter(client => client.uuid !== uuid);
			this.clientsBs$.next(updatedClients);
		});
	}

	public async duplicate$(uuid: string): Promise<Client> {
		return this.withLoading(async () => {
			const duplicatedClient = await firstValueFrom(
				this.api.post<Client>(`${this.BASE_PATH}/duplicate/${uuid}`, {})
			);
			const processedClient = this.processClient(duplicatedClient);
			const currentClients = this.clientsBs$.value;
			const updatedClients = this.processClients([...currentClients, processedClient]);
			this.clientsBs$.next(updatedClients);
			return processedClient;
		});
	}

	public async duplicateBulk$(uuids: string[]): Promise<Client[]> {
		return this.withLoading(async () => {
			const duplicatedClients = await firstValueFrom(
				this.api.post<Client[]>(`${this.BASE_PATH}/duplicate-bulk`, { uuids })
			);
			const processedClients = this.processClients(duplicatedClients);
			const currentClients = this.clientsBs$.value;
			const newUuidSet = new Set(processedClients.map(client => client.uuid));
			const filteredCurrent = currentClients.filter(client => !newUuidSet.has(client.uuid));
			const updatedClients = this.processClients([...processedClients, ...filteredCurrent]);
			this.clientsBs$.next(updatedClients);
			return processedClients;
		});
	}

	public async getClientBySubDomain$(subdomain?: string): Promise<Client> {
		const subdomainToUse: string = subdomain ? subdomain : UrlHelper.getLeftMostSubDomains();
		const clients: Client[] = await firstValueFrom(this.clients$);
		const client: Client | undefined = clients.find(client => client.subdomain === subdomainToUse);

		if (!client) {
			console.warn('clients', clients);
			throw new Error('Could not find client for subdomain: ' + subdomainToUse);
		}

		return client;
	}

	public async selectClient$(clientUuid: string): Promise<void> {
		if (this.selectedClientBs$.value?.uuid === clientUuid || this.pendingSelectedClientUuid === clientUuid) {
			// console.warn('client is the same, skipping select.', {
			// 	currentlySelected: this.selectedClientBs$.value?.uuid,
			// 	pendingSelected: this.pendingSelectedClientUuid,
			// 	requested: clientUuid,
			// });
			return;
		}

		if (this.pendingSelectedClientUuid !== clientUuid) {
			this.pendingSelectedClientUuid = clientUuid;
		}

		const clientWithCoursesAndUnits = await this.getClientWithCoursesAndUnitsAndLessons$(clientUuid);

		this.selectedClientBs$.next(clientWithCoursesAndUnits);
	}

	public async adminSelectClient$(clientUuid: string): Promise<void> {
		const client = await this.get$(clientUuid);

		const tld: string = environment.tld;
		const newUrl: string =
			tld.indexOf('localhost') === 0 ? `http://${tld}` : `https://${client.subdomain}.${tld}/my/sections`;

		console.log('Navigating to new client url:', newUrl);

		await this.nav.navigateByFullUrl(newUrl);
	}

	/**
	 * Exports client users data to CSV
	 * @param clientUuid The UUID of the client whose users should be exported
	 * @param userPoolId The Cognito user pool ID
	 * @returns Promise that resolves when the export is complete
	 */
	public async exportClientUsers$(clientUuid: string, userPoolId: string): Promise<void> {
		return this.withLoading(async () => {
			try {
				// Configure request parameters
				const params = { userPoolId };

				// Make the API request with responseType 'blob'
				const response = await firstValueFrom(
					this.api.getBlob(`cognito/export-client-users/${clientUuid}`, { params })
				);

				if (!response) {
					throw new Error('No response received from server');
				}

				// Create a URL for the blob
				const url = window.URL.createObjectURL(response);

				// Create a temporary link element to trigger the download
				const link = document.createElement('a');
				const timestamp = new Date().toISOString().split('T')[0];
				const filename = `client_export_${clientUuid}_${timestamp}.csv`;

				link.href = url;
				link.download = filename;

				// Trigger the download
				document.body.appendChild(link);
				link.click();

				// Clean up
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} catch (error) {
				console.error('Error exporting client users:', error);
				throw error;
			}
		});
	}
}
